import {useAuth} from '@common/auth/use-auth';
import {useNavigate} from '@common/ui/navigation/use-navigate';
import {message} from '@ui/i18n/message';
import {toast} from '@ui/toast/toast';
import {useEffect, useRef} from 'react';
import {useLocation} from 'react-router';

/**
 * After a customer signs in, if their profile is unfinished, send them to
 * account-settings to complete it. Fires once per app session so it nudges
 * without trapping them.
 *
 * This is now also what carries the deferred registration intake: sign-up asks
 * four things and leaves gender blank, so this gate is what brings people to
 * the application form once they actually have an account. A person who never
 * finishes is still a lead with a name and a phone number — which is the whole
 * reason the questions moved out of the sign-up wizard.
 */
export function ProfileCompletionGate() {
  const {user, isLoggedIn} = useAuth();
  const navigate = useNavigate();
  const {pathname} = useLocation();
  const prompted = useRef(false);

  useEffect(() => {
    if (!isLoggedIn || !user || prompted.current) return;

    // Only nudge customers — never staff/admins.
    const roles: any[] = (user as any).roles || [];
    const isCustomer = roles.some(r => r?.name === 'customers');
    if (!isCustomer) return;

    const missing = !(user as any).gender || !(user as any).birthday;
    if (!missing) return;

    // Don't interrupt auth / checkout / the settings page itself.
    const skipPrefixes = [
      '/account-settings',
      '/login',
      '/register',
      '/checkout',
      '/logout',
      '/forgot-password',
      '/workspace',
    ];
    if (skipPrefixes.some(p => pathname.startsWith(p))) return;

    prompted.current = true;
    toast(
      message('Please take a moment to complete your profile and application.'),
    );
    navigate('/account-settings');
  }, [isLoggedIn, user, pathname, navigate]);

  return null;
}
